/**
 * HEAREN V1 - CANONICAL THEMES (SINGLE SOURCE OF TRUTH)
 * 
 * This file defines the ONE and ONLY taxonomy for themes/categories
 * used across the entire application.
 * 
 * RULES:
 * - Same names everywhere
 * - Same order everywhere (PRODUCT-VALIDATED ORDER)
 * - Labels via i18n (labelKey) - NO hardcoded strings in UI
 * - No "Radio" in theme names (Radio is a CONTEXT, not a theme)
 * - "Favorites" is NOT a theme (it's a view/filter)
 * 
 * PRODUCT-VALIDATED ORDER (DO NOT CHANGE):
 * 1. Nature
 * 2. Relaxation (Breathing & Meditation)
 * 3. Sommeil (Sleep)
 * 4. Méditation (mapped to Breathing & Meditation for Radio)
 * 5. Pulse Flow
 * 6. Classique (Classical)
 * 7. Chill / Lofi
 * 
 * FREE vs PREMIUM GATING (V1):
 * - Free users: Only 2 themes (Nature, Relaxation)
 * - Premium users: All themes
 */

/**
 * Canonical theme IDs - use these everywhere
 */
export const THEME_IDS = {
  NATURE: 'nature',
  RELAXATION: 'relaxation',
  SLEEP: 'sleep',
  MEDITATION: 'meditation',
  PULSE_FLOW: 'pulse_flow',
  CLASSICAL: 'classical',
  CHILL_LOFI: 'chill_lofi',
  // Legacy aliases
  AMBIENT: 'ambient',
  BREATHING_MEDITATION: 'breathing_meditation',
};

/**
 * FREE THEME IDS - per mode (V2 gating)
 * Soundscapes: relaxation, pulse_flow
 * Presence: sleep, chill_lofi
 */
export const FREE_SOUNDSCAPE_THEME_IDS = [
  THEME_IDS.RELAXATION,
  THEME_IDS.PULSE_FLOW,
];

export const FREE_PRESENCE_THEME_IDS = [
  THEME_IDS.SLEEP,
  THEME_IDS.CHILL_LOFI,
];

// Combined free themes (for backwards compat)
export const FREE_THEME_IDS = [
  ...new Set([...FREE_SOUNDSCAPE_THEME_IDS, ...FREE_PRESENCE_THEME_IDS])
];

// Max tracks per theme for free users
export const FREE_TRACKS_PER_THEME = 5;

// Max favorites for free users
export const FREE_FAVORITES_LIMIT = 5;

/**
 * Check if a theme is available for Free users in a given mode
 * @param {string} themeId - Theme ID to check
 * @param {string} mode - 'soundscapes' or 'presence' (defaults to soundscapes)
 * @returns {boolean} True if theme is free for this mode
 */
export const isThemeFree = (themeId, mode = 'soundscapes') => {
  const normalizedId = normalizeThemeId(themeId);
  const freeIds = mode === 'presence' ? FREE_PRESENCE_THEME_IDS : FREE_SOUNDSCAPE_THEME_IDS;
  return freeIds.includes(normalizedId);
};

/**
 * Check if a theme requires Premium
 * @param {string} themeId - Theme ID to check
 * @param {string} mode - 'soundscapes' or 'presence'
 * @returns {boolean} True if theme requires Premium
 */
export const isThemePremium = (themeId, mode = 'soundscapes') => {
  return !isThemeFree(themeId, mode);
};

/**
 * Get themes filtered by user's premium status
 * @param {boolean} isPremium - Whether user has Premium
 * @returns {Array} Filtered themes
 */
export const getThemesForUser = (isPremium) => {
  if (isPremium) {
    return CANONICAL_THEMES;
  }
  return CANONICAL_THEMES.filter(theme => FREE_THEME_IDS.includes(theme.id));
};

/**
 * Canonical themes list - PRODUCT-VALIDATED ORDER
 * Order: Nature, Relaxation, Sommeil, Méditation, Pulse Flow, Classique, Chill / Lofi
 * 
 * Each theme has:
 * - id: stable key for API/data
 * - order: explicit sort order (1-indexed for clarity)
 * - labelKey: i18n key for translation (use t(theme.labelKey))
 * - icon: emoji for quick visual identification
 * - bgImage: background image URL
 */
export const CANONICAL_THEMES = [
  {
    id: 'nature',
    order: 1,
    labelKey: 'themes.nature',
    name: 'Nature', // Fallback only - use labelKey for display
    icon: '🌿',
    bgImage: 'https://images.unsplash.com/photo-1716896497110-d74bc1b53bdd?w=800&q=80',
  },
  {
    id: 'relaxation',
    order: 2,
    labelKey: 'themes.relaxation',
    name: 'Relaxation',
    icon: '🧘',
    bgImage: 'https://images.unsplash.com/photo-1655192151331-ce584a4ba037?w=800&q=80',
  },
  {
    id: 'sleep',
    order: 3,
    labelKey: 'themes.sleep',
    name: 'Sleep',
    icon: '🌙',
    bgImage: 'https://images.unsplash.com/photo-1531956801324-df716337e0b9?w=800&q=80',
  },
  {
    id: 'meditation',
    order: 4,
    labelKey: 'themes.meditation',
    name: 'Meditation',
    icon: '🧘‍♀️',
    bgImage: 'https://images.unsplash.com/photo-1655192151331-ce584a4ba037?w=800&q=80',
  },
  {
    id: 'pulse_flow',
    order: 5,
    labelKey: 'themes.pulseFlow',
    name: 'Pulse Flow',
    icon: '⚡',
    bgImage: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800&q=80',
  },
  {
    id: 'classical',
    order: 6,
    labelKey: 'themes.instrumental',
    name: 'Instrumental',
    icon: '🎻',
    bgImage: 'https://images.unsplash.com/photo-1644489776594-87f20f232d1b?w=800&q=80',
  },
  {
    id: 'chill_lofi',
    order: 7,
    labelKey: 'themes.lofi',
    name: 'Lofi',
    icon: '🎧',
    bgImage: 'https://images.unsplash.com/photo-1738687831691-85cfa18510dd?w=800&q=80',
  },
  //{
    //id: 'deep_calm',
    //order: 8,
    //labelKey: 'themes.deepCalm',
    //name: 'Deep Calm',
    //icon: '🌊',
    //bgImage: '/themes/deep_calm.avif',
  //},
];

/**
 * Legacy ID mapping - maps old IDs to new canonical IDs
 * Used for backward compatibility with existing data/API
 */
export const LEGACY_ID_MAP = {
  // French legacy IDs
  'classique': 'classical',
  'piano_ambiant': 'relaxation',
  'ambient': 'relaxation',
  'respiration': 'relaxation',
  'breathing_meditation': 'meditation',
  'sommeil': 'sleep',
  // Radio legacy IDs
  'radio_classical': 'classical',
  'radio_classique': 'classical',
  'radio_ambient': 'relaxation',
  'radio_focus': 'relaxation',
  'radio_chill': 'chill_lofi',
  'radio_chill_lofi': 'chill_lofi',
  'radio_relax': 'relaxation',
  'radio_breathing': 'meditation',
  'radio_meditation': 'meditation',
  'radio-breathing-meditation': 'meditation',
  'radio_breathing_meditation': 'meditation',
  'radio_nature': 'nature',
  'radio_sleep': 'sleep',
  'radio_sommeil': 'sleep',
  'radio_pulse': 'pulse_flow',
  'radio_pulse_flow': 'pulse_flow',
  // Soundscapes legacy IDs
  'Nature': 'nature',
  'Relaxation': 'relaxation',
  'Sleep': 'sleep',
  'Meditation': 'meditation',
  'Classical': 'classical',
  'Chill': 'chill_lofi',
  'ChillLofi': 'chill_lofi',
  'PulseFlow': 'pulse_flow',
  // V11 card IDs → canonical player IDs
  'instrumental': 'classical',
  'lofi': 'chill_lofi',
};

/**
 * Normalize any theme ID to canonical ID
 */
export const normalizeThemeId = (id) => {
  if (!id) return null;
  const lowerId = id.toLowerCase();
  return LEGACY_ID_MAP[id] || LEGACY_ID_MAP[lowerId] || id;
};

/**
 * Get theme by ID (supports both canonical and legacy IDs)
 */
export const getThemeById = (id) => {
  const canonicalId = normalizeThemeId(id);
  return CANONICAL_THEMES.find(theme => theme.id === canonicalId);
};

/**
 * Get translated theme label
 * @param {string} id - Theme ID (canonical or legacy)
 * @param {object|function} t - i18n translations object or function
 * @returns {string} Translated label or fallback name
 */
export const getThemeLabel = (id, t) => {
  const theme = getThemeById(id);
  if (!theme) return id;
  
  // Use i18n if available
  if (t) {
    // t is an object (from useLanguage hook) - access nested keys
    if (typeof t === 'object') {
      const keys = theme.labelKey.split('.');
      let value = t;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }
      if (value && typeof value === 'string') return value;
    }
    // t is a function (i18next style)
    if (typeof t === 'function') {
      const translated = t(theme.labelKey);
      if (translated && translated !== theme.labelKey) {
        return translated;
      }
    }
  }
  
  // Fallback to English name
  return theme.name;
};

/**
 * Get theme name by ID (DEPRECATED - use getThemeLabel instead)
 * Kept for backward compatibility
 */
export const getThemeName = (id) => {
  const theme = getThemeById(id);
  return theme ? theme.name : id;
};

/**
 * Get all themes sorted by canonical order
 */
export const getThemes = () => {
  return [...CANONICAL_THEMES].sort((a, b) => a.order - b.order);
};

/**
 * Sort an array of items by canonical theme order
 * @param {Array} items - Items to sort
 * @param {function} getIdFn - Function to extract theme ID from item
 * @returns {Array} Sorted items
 */
export const sortByThemeOrder = (items, getIdFn = (item) => item.id) => {
  const orderMap = {};
  CANONICAL_THEMES.forEach(theme => {
    orderMap[theme.id] = theme.order;
  });
  
  return [...items].sort((a, b) => {
    const aId = normalizeThemeId(getIdFn(a));
    const bId = normalizeThemeId(getIdFn(b));
    
    const aOrder = orderMap[aId] ?? 999;
    const bOrder = orderMap[bId] ?? 999;
    
    return aOrder - bOrder;
  });
};

/**
 * Get canonical order index for a theme ID
 */
export const getThemeOrder = (id) => {
  const theme = getThemeById(id);
  return theme ? theme.order : 999;
};

export default CANONICAL_THEMES;
